import { useEffect, useMemo, useState } from "react";
import * as lookups from "../../data/lookups";
import api from "../../api/axios";
import MultiSelect from "./MultiSelect";
import SearchableSelect from "./SearchableSelect";
import useAuthStore from "../../store/auth.store";
import PowerplayOverrideField from "./PowerplayOverrideField";

export default function FormRenderer({ step, values, onChange }) {
  const [optionsMap, setOptionsMap] = useState({});
  const user = useAuthStore((s) => s.user);
  const isOrg = user?.account_type === "organization" || user?.account_type_v2 === "B2B";

  // Filter out fields the schema marks `hideForB2C: true` when the current
  // user is a B2C / Solo account. CI Player IDs and similar archive-flow
  // identifiers don't apply to a freelance scorer.
  const visibleFields = useMemo(
    () => (step.fields || []).filter((f) => !(f.hideForB2C && !isOrg)),
    [step.fields, isOrg],
  );

  useEffect(() => {
    visibleFields.forEach((field) => {
      if (field.fetch && !optionsMap[field.name]) {
        api.get(field.fetch).then((res) => {
          const raw = res.data?.results ?? res.data;
          let list = Array.isArray(raw) ? raw : [];
          // Per-field option filter: schema can list option labels to drop
          // for B2C accounts (e.g. Test format, archive-only options).
          if (!isOrg && Array.isArray(field.hideOptionLabelsForB2C)) {
            const drop = new Set(
              field.hideOptionLabelsForB2C.map((s) => s.toLowerCase()),
            );
            list = list.filter((i) => !drop.has((i.name || "").toLowerCase()));
          }
          setOptionsMap((prev) => ({
            ...prev,
            [field.name]: list.map((i) => ({
              // Prefer display_label (umpire serializer provides this
              // — "Display Name" / "First Last" / "First") so umpire
              // dropdowns on the match-create + edit forms never show
              // just the first name. Falls back to plain `name` for
              // every other entity (teams, stadiums, competitions, …)
              // that doesn't define display_label.
              label: i.display_label || i.name,
              value: i.id,
            })),
          }));
        });
      }
    });
  }, [visibleFields, isOrg]);

  const getOptions = (field) => {
    if (field.optionsKey) {
      return lookups[field.optionsKey] || [];
    }

    if (field.fetch) {
      return optionsMap[field.name] || [];
    }

    return [];
  };


  return (
    <div>
      {/* Optional step description shown above the field grid. Schemas
          opt in by setting `description` on the step. Used 2026-06-05
          to hint operators on the rename-capture step that the fields
          are only needed when actually renaming. */}
      {step.description && (
        <p className="px-6 sm:px-8 pt-2 pb-3 text-xs text-text-secondary leading-relaxed">
          {step.description}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5 px-6 sm:px-8 pt-2">
      {visibleFields.map((field) => {
        const value = values[field.name] ?? (field.type === "multiselect" ? [] : "");

        return (
          <div key={field.name} className="flex flex-col gap-1.5">
            {/* LABEL — clean text; required-field validation is still
                enforced via AddTemplate's step-validation (missing
                fields listed inline if Save is attempted without
                them) but no per-label marker clutters the form. */}
            <label className="text-[11px] uppercase tracking-wider opacity-70">
              {field.displayName || field.name}
            </label>

            {/* TEXT INPUT */}
            {field.type === "text" && (
              <input
                type={field.inputType || "text"}
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                readOnly={!!field.readOnly}
                placeholder={field.placeholder || (field.readOnly && !value ? "auto-generated on save" : undefined)}
                title={field.readOnly ? "Server-generated · read-only" : undefined}
                className={`py-2 px-3 h-[44px] border border-gray-700 rounded text-sm focus:outline-none focus:border-button-primary ${
                  field.readOnly
                    ? "bg-input-secondary/60 text-text-secondary font-mono cursor-not-allowed"
                    : "bg-input-secondary"
                }`}
              />
            )}

            {/* TEXTAREA — multi-line free text. Added 2026-06-05 for the
                team-rename reason field; reusable for any longer-form
                description field on entity create/edit. */}
            {field.type === "textarea" && (
              <textarea
                rows={field.rows || 3}
                value={value}
                onChange={(e) => onChange(field.name, e.target.value)}
                readOnly={!!field.readOnly}
                placeholder={field.placeholder || undefined}
                className={`py-2 px-3 border border-gray-700 rounded text-sm focus:outline-none focus:border-button-primary resize-y ${
                  field.readOnly
                    ? "bg-input-secondary/60 text-text-secondary cursor-not-allowed"
                    : "bg-input-secondary"
                }`}
              />
            )}

            {/* SELECT — searchable typeahead. Replaces the native <select>
                so long lists (stadiums, teams, umpires, competitions,
                match types …) are usable. Behaves identically to a
                native select for tiny lists (gender, batting hand). */}
            {field.type === "select" && (
              <SearchableSelect
                options={getOptions(field)}
                value={value}
                onChange={(v) => onChange(field.name, v)}
                placeholder={`Select ${field.displayName?.toLowerCase() || ""}…`}
              />
            )}

            {/* MULTI SELECT */}
            {field.type === "multiselect" && (
              <MultiSelect
                options={getOptions(field)}
                value={Array.isArray(value) ? value : []}
                onChange={(next) => onChange(field.name, next)}
                placeholder={`Select ${field.displayName?.toLowerCase() || ""}…`}
              />
            )}

            {/* BOOLEAN — styled toggle that submits true/false. Used for
                whatsapp_ok, contact_whatsapp_ok, and similar yes/no flags
                where the serializer expects a real boolean (not "yes" string). */}
            {field.type === "boolean" && (
              <button
                type="button"
                onClick={() => onChange(field.name, !value)}
                className={`h-[44px] px-3 rounded border text-sm text-left flex items-center gap-2 ${
                  value
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-input-secondary border-gray-700 text-text-secondary hover:text-text-primary"
                }`}
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-current text-xs">
                  {value ? "✓" : ""}
                </span>
                <span>{value ? "Yes" : "No"}</span>
              </button>
            )}

            {/* POWERPLAY OVERRIDE — CF-P1-04. Custom widget for
                Match.powerplay_override JSONField. Shape depends on the
                format the operator picked above:
                  T20 / T10 / Hundred → {"powerplay_overs": <int>}
                  ODI → {"pp1":[start,end],"pp2":[...],"pp3":[...]}
                Empty / 0 values produce an empty object so the
                serializer treats it as "use format default" (matches
                backend semantics where is_rain_affected=False ignores
                this field). The widget reads sibling field
                values.match_type to decide which shape to render —
                operator can flip match_type and the widget updates. */}
            {field.type === "powerplay_override" && (
              <PowerplayOverrideField
                value={value && typeof value === "object" ? value : {}}
                onChange={(next) => onChange(field.name, next)}
                matchTypeId={values?.match_type}
                isActive={!!values?.is_rain_affected}
              />
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
